import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceArea,
  Tooltip,
  Legend,
} from "recharts";
import {
  Heart,
  Wind,
  Activity,
  Droplets,
  Thermometer,
  TriangleAlert,
  Download,
  Printer,
  Calendar,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { database } from "../../../firebase";
import { ref, onValue, off } from "firebase/database";
import { useParams, useNavigate, Link } from "react-router-dom";
import Alerts from "./alerts";
import constant from "../../../constant";

const getLatestEWS=(ewsData)=> {
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  let ews = 0;
  const recentEwsData = ewsData.filter(
    (item) => item.timestamp >= oneHourAgo && item.value !== null
  );
  if (recentEwsData.length > 0) {
    ews = recentEwsData[recentEwsData.length - 1].value;
    return ews;
  }
  return 0;
}

const PatientTrendsDashboard = () => {
  const { hospId, patientId, bedId } = useParams();
  const navigate = useNavigate();

  const [vitalSigns, setVitalSigns] = useState({
    ews: { current: 0, data: [], unit: "Count" },
    hr: { current: 0, data: [], unit: "BPM" },
    resp_rate: { current: 0, data: [], unit: "RPM" },
    nibp_systolic: { current: 0, data: [], unit: "mmHg" },
    nibp_diastolic: { current: 0, data: [], unit: "mmHg" },
    spo2: { current: 0, data: [], unit: "%" },
    temp: { current: 0, data: [], unit: "°C" },
  });

  const [isLoading, setIsLoading] = useState(true);
  const [latestEws, setLatestEws] = useState(0);
  const [error, setError] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState("morning");
  const [patient, setPatient] = useState([]);
  const [timeTicks, setTimeTicks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");

        if (!token || !hospId) {
          alert("No authentication token or hospital ID found");
          return;
        }

        const patientUrl = `${constant.REACT_APP_API_URL}/hospital/${hospId}/patient/${patientId}`;
        const patientResponse = await fetch(patientUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!patientResponse.ok) {
          const message = await patientResponse.text();
          throw new Error(message || "Failed to fetch patient data");
        }

        const patientResult = await patientResponse.json();
        setPatient(patientResult);
      } catch (error) {
        setError(error.message || "An error occurred while fetching data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [hospId, patientId]);


  // ####
  const vitalRanges = {
    ews: {
      normal: [0, 3],
      moderate: [3, 6],
      yAxis: { min: 0, max: 15 },
    },
    hr: {
      normal: [60, 100],
      moderate: [50, 120],
      yAxis: { min: 20, max: 150 },
    },
    resp_rate: {
      normal: [12, 20],
      moderate: [9, 24],
      yAxis: { min: 2, max: 40 },
    },
    nibp_systolic: {
      normal: [90, 120],
      moderate: [70, 139],
      yAxis: { min: 40, max: 220 },
    },
    nibp_diastolic: {
      normal: [60, 80],
      moderate: [50, 89],
      yAxis: { min: 30, max: 150 },
    },
    spo2: {
      normal: [95, 100],
      moderate: [90, 100],
      yAxis: { min: 60, max: 100 },
    },
    temp: {
      normal: [36.1, 37.5],
      moderate: [35.5, 38.0],
      yAxis: { min: 33.0, max: 40.0 },
    },
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    try {
      const url = `${constant.REACT_APP_API_URL}/hospital/${hospId}/patient/${patientId}/vitals/download`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Download failed");

      const csv = await res.text();
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `vitals_report_${patient.name}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert("Failed to download report");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentPeriodIST = () => {
    const now = new Date();
    const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    const hours = istTime.getUTCHours();
    return hours < 12 ? "morning" : "evening";
  };

  const getTodayTimestampsIST = () => {
    const now = new Date();
    const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    const startOfDayIST = new Date(istTime);
    startOfDayIST.setUTCHours(0, 0, 0, 0);
    const startOfDay = Math.floor(
      (startOfDayIST.getTime() - 5.5 * 60 * 60 * 1000) / 1000
    );
    const endOfDay = startOfDay + 86400;
    return { startOfDay, endOfDay };
  };

  const getPeriodTimestampsIST = (period, dateStr) => {
    const localDate = new Date(dateStr);
    const istDate = new Date(localDate.getTime() + 5.5 * 60 * 60 * 1000);
    const startOfDayIST = new Date(istDate);
    startOfDayIST.setUTCHours(0, 0, 0, 0);
    const startOfDayUTCms = startOfDayIST.getTime() - 5.5 * 60 * 60 * 1000;
    const startOfDay = Math.floor(startOfDayUTCms / 1000);

    if (period === "morning") {
      return {
        start: startOfDay,
        end: startOfDay + Math.floor(12.5 * 3600),
      };
    } else {
      return {
        start: startOfDay + Math.floor(12 * 3600),
        end: startOfDay + Math.floor(24.5 * 3600),
      };
    }
  };

  const calculateMedian = (values) => {
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  };

  const processDataByIntervals = (data, period, dateStr) => {
    const { start, end } = getPeriodTimestampsIST(period, dateStr);
    const intervals = [];

    for (let time = start; time < end; time += 1800) {
      const intervalEnd = time + 1800;
      const intervalData = data.filter((item) => {
        const itemTime = Math.floor(item.timestamp.getTime() / 1000);
        return itemTime >= time && itemTime < intervalEnd;
      });

      const istTimestamp = new Date((time + 5.5 * 60 * 60) * 1000);
      const displayTime = istTimestamp.toLocaleTimeString("en-US", {
        hour12: true,
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
      });

      if (intervalData.length > 0) {
        let medianValue = calculateMedian(
          intervalData.map((item) => item.value)
        );
        intervals.push({
          timestamp: new Date(time * 1000),
          value: medianValue,
          time: displayTime,
          hasData: true,
        });
      } else {
        intervals.push({
          timestamp: new Date(time * 1000),
          value: null,
          time: displayTime,
          hasData: false,
        });
      }
    }

    return intervals;
  };

  useEffect(() => {
    const currentISTPeriod = getCurrentPeriodIST();
    setCurrentPeriod(currentISTPeriod);
    const intervals = processDataByIntervals(
      [],
      currentISTPeriod,
      selectedDate
    );
    setTimeTicks(intervals.map((item) => item.time));
  }, [selectedDate]);

  useEffect(() => {
    const fetchVitals = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token || !hospId || !patientId) {
          return;
        }
        const vitalsUrl = `${constant.REACT_APP_API_URL}/hospital/${hospId}/patient/${patientId}/vitals?vital_date=${selectedDate}`;
        const response = await fetch(vitalsUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch vitals data");
        }
        const data = await response.json();
        const vitalsData = {
          ews: [],
          hr: [],
          resp_rate: [],
          nibp_systolic: [],
          nibp_diastolic: [],
          spo2: [],
          temp: [],
        };
        data.vitals.forEach((v) => {
          const ts = new Date(v.recorded_on);
          vitalsData.ews.push({ timestamp: ts, value: v.ews });
          vitalsData.hr.push({ timestamp: ts, value: v.heart_rate });
          vitalsData.resp_rate.push({
            timestamp: ts,
            value: v.respiratory_rate,
          });
          vitalsData.nibp_systolic.push({
            timestamp: ts,
            value: v.blood_pressure_systolic,
          });
          vitalsData.nibp_diastolic.push({
            timestamp: ts,
            value: v.blood_pressure_diastolic,
          });
          vitalsData.spo2.push({ timestamp: ts, value: v.oxygen_saturation });
          vitalsData.temp.push({ timestamp: ts, value: v.temperature });
        });
        Object.keys(vitalsData).forEach((key) => {
          vitalsData[key].sort((a, b) => a.timestamp - b.timestamp);
        });
      let latestEws = getLatestEWS(vitalsData.ews);
        if (latestEws !== -1) {
          setLatestEws(latestEws);
        }
        // Process for current period
        setVitalSigns((prev) => {
          const newVitals = { ...prev };
          [
            "ews",
            "hr",
            "resp_rate",
            "nibp_systolic",
            "nibp_diastolic",
            "spo2",
            "temp",
          ].forEach((vital) => {
            const processedData = processDataByIntervals(
              vitalsData[vital],
              currentPeriod,
              selectedDate
            );
            newVitals[vital] = { ...newVitals[vital], data: processedData };
          });
          newVitals["ews"] = { ...newVitals["ews"], current: latestEws };

          return newVitals;
        });

        const intervals = processDataByIntervals(
          [],
          currentPeriod,
          selectedDate
        );
        setTimeTicks(intervals.map((item) => item.time));
      } catch (err) {
        setError(err.message);
      }
    };

    fetchVitals();
  }, [selectedDate, currentPeriod, hospId, patientId]);

  useEffect(() => {
    if (!hospId || !bedId) {
      setIsLoading(false);
      return;
    }

    const dbRefs = {};
    const { startOfDay, endOfDay } = getTodayTimestampsIST();

    const patientRef = ref(database, `hospital/${hospId}/${bedId}/patientInfo`);
    onValue(patientRef, (snapshot) => {
      const data = snapshot.val();
      setPatientInfo(data);
    });

    const vitalTypes = ["ews", "hr", "resp_rate", "nibp", "spo2", "temp"];

    vitalTypes.forEach((vital) => {
      const vitalRef = ref(database, `hospital/${hospId}/${bedId}/${vital}`);
      dbRefs[vital] = vitalRef;

      onValue(vitalRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const todayData = Object.entries(data)
            .map(([key, value]) => {
              if (vital === "nibp") {
                // Parse the nibp string (e.g., "154/102") into systolic and diastolic
                const [systolic, diastolic] = value.value
                  .split("/")
                  .map(Number);
                return {
                  timestamp: new Date(value.time * 1000),
                  value: { systolic, diastolic },
                  time: new Date(value.time * 1000).toLocaleTimeString(
                    "en-US",
                    {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  ),
                };
              }
              return {
                timestamp: new Date(value.time * 1000),
                value: value.value,
                time: new Date(value.time * 1000).toLocaleTimeString("en-US", {
                  hour12: false,
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              };
            })
            .filter((item) => {
              const itemTime = Math.floor(item.timestamp.getTime() / 1000);
              return itemTime >= startOfDay && itemTime < endOfDay;
            })
            .sort((a, b) => a.timestamp - b.timestamp);

          const currentValue =
            todayData.length > 0
              ? todayData[todayData.length - 1].value
              : vital === "nibp"
              ? { systolic: 0, diastolic: 0 }
              : 0;

          setVitalSigns((prev) => ({
            ...prev,
            [vital === "nibp" ? "nibp_systolic" : vital]: {
              ...prev[vital === "nibp" ? "nibp_systolic" : vital],
              current: vital === "nibp" ? currentValue.systolic : currentValue,
            },
            ...(vital === "nibp"
              ? {
                  nibp_diastolic: {
                    ...prev.nibp_diastolic,
                    current: currentValue.diastolic,
                  },
                }
              : {}),
          }));
        }
        setIsLoading(false);
      });
    });

    return () => {
      Object.values(dbRefs).forEach((dbRef) => off(dbRef));
      off(patientRef);
    };
  }, [hospId, bedId]);

  const getRiskLevel = (value, vitalType) => {
    const ranges = vitalRanges[vitalType];
    if (!ranges) return "normal";

    if (value >= ranges.normal[0] && value <= ranges.normal[1]) return "normal";
    if (value >= ranges.moderate[0] && value <= ranges.moderate[1]) return "moderate";

    // if (value >= ranges.normal[0] && value <= ranges.normal[1]) return 'normal';
    // if (value >= ranges.moderate[0] && value <= ranges.moderate[1]) return 'moderate';
    return "high";
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "normal":
        return "text-green-600 bg-green-50 border-green-200";
      case "moderate":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const formatValue = (vital, value) => {
    if (vital === "temp") {
      return value?.toFixed(1) || "0.0";
    }
    return Math.round(value || 0);
  };

  const vitalConfigs = [
    { key: "ews", icon: AlertCircle, label: "EWS", unit: "Count" },
    { key: "hr", icon: Heart, label: "HR", unit: "BPM" },
    { key: "resp_rate", icon: Wind, label: "RR", unit: "RPM" },
    { key: "nibp_systolic", icon: Activity, label: "BP(Sys)", unit: "mmHg" },
    { key: "nibp_diastolic", icon: Activity, label: "BP(Dia)", unit: "mmHg" },
    { key: "spo2", icon: Droplets, label: "SpO₂", unit: "%" },
    { key: "temp", icon: Thermometer, label: "Temp", unit: "°C" },
  ];

  const togglePeriod = () => {
    setCurrentPeriod((prev) => (prev === "morning" ? "evening" : "morning"));
  };

  const handlePrint = () => {
    window.print();
  };

  const CustomTooltip = ({ active, payload, label, vitalLabel }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="bg-white border border-gray-300 rounded-md shadow-md p-2 text-xs"
          style={{ lineHeight: "1.3" }}
        >
          <p className="font-semibold">{`Vital: ${vitalLabel}`}</p>
          {/* <p>{`Time: ${label}`}</p> */}
          <p>{`Value: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  const renderRiskBands = (ranges, vitalKey) => {
    if (vitalKey === "ews") {
      return (
        <>
          <ReferenceArea
            y1={ranges.yAxis.min}
            y2={ranges.normal[1]}
            fill="#17a34a"
            fillOpacity={0.4}
          />
          <ReferenceArea
            y1={ranges.normal[1]}
            y2={ranges.moderate[1]}
            fill="#ebb105"
            fillOpacity={0.4}
          />
          <ReferenceArea
            y1={ranges.moderate[1]}
            y2={ranges.yAxis.max}
            fill="#FF0000"
            fillOpacity={0.4}
          />
        </>
      );
    }

    return (
      <>
        <ReferenceArea
          y1={ranges.yAxis.min}
          y2={ranges.moderate[0]}
          fill="#FF0000"
          fillOpacity={0.4}
        />
        <ReferenceArea
          y1={ranges.moderate[0]}
          y2={ranges.normal[0]}
          fill="#ebb105"
          fillOpacity={0.4}
        />
        <ReferenceArea
          y1={ranges.normal[0]}
          y2={ranges.normal[1]}
          fill="#17a34a"
          fillOpacity={0.4}
        />
        <ReferenceArea
          y1={ranges.normal[1]}
          y2={ranges.moderate[1]}
          fill="#ebb105"
          fillOpacity={0.4}
        />
        <ReferenceArea
          y1={ranges.moderate[1]}
          y2={ranges.yAxis.max}
          fill="#FF0000"
          fillOpacity={0.4}
        />
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (!hospId || !bedId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No patient selected</p>
        </div>
      </div>
    );
  }

  const hasAnyValidData = vitalConfigs.some((config) =>
    vitalSigns[config.key].data.some(
      (item) => item.hasData && item.value !== null
    )
  );

  return (
    <>
      <style>
        {`
    @media print {
        * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        body * {
            visibility: hidden;
        }
        .printable-content,
        .printable-content * {
            visibility: visible;
        }
        .printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 1cm;
            font-size: 12pt;
            background: white !important;
        }
        .no-print {
            display: none !important;
        }
        .flex-col,
        .lg\\:flex-row {
            display: flex !important;
            flex-direction: row !important;
        }
        .lg\\:basis-10\\/12 {
            flex: 5 !important;
        }
        .lg\\:basis-2\\/12 {
            flex: 1 !important;
        }
        .h-32 {
            height: 160px !important;
        }
        .text-3xl {
            font-size: 18pt !important;
        }
        .text-base, .text-sm, .text-xs {
            font-size: 10pt !important;
        }
        .rounded-lg {
            border-radius: 8px !important;
        }
        .shadow-lg {
            box-shadow: none !important;
        }
        .bg-gray-100,
        .bg-green-50,
        .bg-yellow-50,
        .bg-red-50,
        .bg-blue-500,
        .bg-blue-600,
        .bg-green-400,
        .bg-yellow-400,
        .bg-red-400 {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        .text-green-600,
        .text-yellow-600,
        .text-red-600,
        .text-gray-600 {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        .mb-6 {
            margin-bottom: 1cm !important;
        }
        .p-6 {
            padding: 1cm !important;
        }
        .h-5, .w-5, .h-4, .w-4 {
            width: 16pt !important;
            height: 16pt !important;
        }
    }
`}
      </style>

      <div className="printable-content bg-white rounded-lg shadow-lg p-6 flex flex-col lg:flex-row gap-6">
        <div className="flex-1 lg:basis-10/12">
          <div className="mb-6 bg-gray-100 p-4 rounded-lg flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="items-center space-x-4">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-2 flex items-center">
                  <button
                    onClick={() => navigate(-1)}
                    className="text-sm brand-border flex justify-center items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300  px-3  no-print"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    All Patients
                  </button>
                </div>
                <div className="col-span-4 text-sm">
                  <p className="text-gray-600 font-medium">
                    Patient Name:{" "}
                    <span className="capitalize">{patient.name || "N/A"}</span>
                  </p>
                  <p className="text-gray-600 font-medium">
                    Patient ID:{" "}
                    <span>{patient.patient_hospital_id || "N/A"}</span>
                  </p>
                </div>
                <div className="col-span-4 text-sm">
                  <p className="text-gray-600 font-medium">
                    Bed #: <span>{patient.bed_number || "N/A"}</span>
                  </p>
                  <p className="text-gray-600 font-medium">
                    Admitted On:{" "}
                    <span>
                      {patient.admitted_on
                        ? new Date(patient.admitted_on).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : "N/A"}
                    </span>
                  </p>
                  <p className="text-gray-600 font-medium">
                    Consulting Doctor:{" "}
                    <span className="capitalize">
                      {patient.doctor?.name || "N/A"}
                    </span>
                  </p>
                </div>
                <div className="col-span-2 flex items-center justify-end">
                  <button
                    onClick={handlePrint}
                    className=" brand-border flex justify-center text-sm items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300  px-3   no-print"
                  >
                    <Printer className="mr-2" />
                    Print Report
                  </button>
                </div>
              </div>
            </div>
            <div className="flex space-x-4 mt-4 lg:mt-0"></div>
          </div>

          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold text-gray-800 ">
              Trends & Report
            </h2>
            <p className="text-xs italic text-gray-800">
              *Vital parameter value are 30 minutes average
            </p>
          </div>

          <div className="flex items-center justify-between space-x-4 mb-4">
            <div className="text-xs flex items-center">
              <span className="inline-block w-3 h-3 bg-green-400 rounded-full mr-1"></span>
              Normal
              <span className="inline-block w-3 h-3 bg-yellow-400 rounded-full mr-1 ml-3"></span>
              Moderate Risk
              <span className="inline-block w-3 h-3 bg-red-400 rounded-full mr-1 ml-3"></span>
              High Risk
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={togglePeriod}
                disabled={currentPeriod === "morning"}
                className={`flex items-center px-3 py-1 rounded no-print ${
                  currentPeriod === "morning"
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-blue-600 hover:bg-blue-50"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-800">
                  {currentPeriod === "morning"
                    ? "12:00 AM - 11:59 AM"
                    : "12:00 PM - 11:59 PM"}
                </p>
              </div>
              <button
                onClick={togglePeriod}
                disabled={currentPeriod === "evening"}
                className={`flex items-center px-3 py-1 rounded no-print ${
                  currentPeriod === "evening"
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-blue-600 hover:bg-blue-50"
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-gray-100 text-gray-800 cursor-pointer text-xs px-4 py-2 rounded-lg hover:bg-gray-300 transition no-print"
              />
            </div>
          </div>
          {vitalConfigs.map((config, index) => {
            const vital = vitalSigns[config.key];
            const chartData = vital.data.map((item) => ({
              time: item.time,
              value: item.value,
              hasData: item.hasData,
            }));

            const chartDefaultData = vital.data.map((item) => ({
              time: item.time,
              value: vitalRanges[config.key].yAxis.min,
              hasData: true,
            }));
            const hasValidData = chartData.some(
              (item) => item.hasData && item.value !== null
            );

            const ranges = vitalRanges[config.key];
            const currentValue = formatValue(config.key, vital.current);
            const riskLevel = getRiskLevel(vital.current || 0, config.key);
            const isFirstWithData =
              hasAnyValidData &&
              hasValidData &&
              !vitalConfigs
                .slice(0, index)
                .some((cfg) =>
                  vitalSigns[cfg.key].data.some(
                    (item) => item.hasData && item.value !== null
                  )
                );

            const isFirstVital = index === 0;

            return (
              <div key={config.key} className="flex flex-col lg:flex-row mb-6">
                <div
                  className={`flex-1 lg:basis-1/12 flex flex-col justify-center border rounded-lg ${getRiskColor(
                    riskLevel
                  )}`}
                >
                  <div className="flex flex-row gap-2 items-center justify-center p-2">
                    <config.icon className="h-5 w-5 mb-1" />
                    <span className="text-xs font-medium">{config.label}</span>
                  </div>
                  <div className="flex flex-col items-center justify-center p-2">
                    <span className="text-base text-3xl font-bold">
                      {currentValue}
                    </span>
                    <span className="text-xs text-gray-600">{config.unit}</span>
                  </div>
                </div>
                <div className="flex-1 lg:basis-10/12 rounded-lg">
                  <div className="h-32 relative">
                    {hasValidData ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartData}
                          margin={{
                            top: isFirstWithData ? -10 : 0,
                            right: 10,
                            left: 10,
                            bottom: 10,
                          }}
                        >
                          {isFirstWithData && (
                            <XAxis
                              orientation="top"
                              dataKey="time"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 10 }}
                              interval="preserveStartEnd"
                            />
                          )}
                          <YAxis
                            domain={[ranges.yAxis.min, ranges.yAxis.max]}
                            tick={{ fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                          />

                          {/* <Tooltip /> */}
                          <Tooltip
                            content={
                              <CustomTooltip vitalLabel={config.label} />
                            }
                          />

                          {/* <Line type="monotone" dataKey="time" stroke="#82ca9d" activeDot={{ r: 8 }} /> */}

                          {renderRiskBands(ranges, config.key)}
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#000000"
                            strokeWidth={1}
                            dot={{ fill: "#000000", strokeWidth: 1, r: 2 }}
                            activeDot={{ r: 5, fill: "#000000" }}
                            connectNulls={true}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={chartDefaultData}
                            margin={{
                              top: isFirstVital ? -10 : 0,
                              right: 10,
                              left: 10,
                              bottom: 10,
                            }}
                          >
                            {isFirstVital && (
                              <XAxis
                                orientation="top"
                                dataKey="time"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10 }}
                                interval="preserveStartEnd"
                              />
                            )}
                            <YAxis
                              domain={[ranges.yAxis.min, ranges.yAxis.max]}
                              tick={{ fontSize: 10 }}
                              axisLine={false}
                              tickLine={false}
                            />
                            {renderRiskBands(ranges, config.key)}
                            <Line
                              type="monotone"
                              dataKey="value"
                              // stroke="#000000"
                              strokeWidth={0}
                              dot={{ fill: "#000000", strokeWidth: 0, r: 0 }}
                              activeDot={{ r: 0, fill: "#000000" }}
                              // connectNulls={true}
                              // isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {hasAnyValidData === false && (
            <div className="h-10 relative">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[]}
                  margin={{ top: -10, right: 10, left: 10, bottom: 10 }}
                >
                  <XAxis
                    orientation="top"
                    dataKey="time"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                    ticks={timeTicks}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="relative group flex justify-end no-print">
            <button
              onClick={handleDownload}
              disabled={isLoading}
              className="text-sm brand-border flex justify-center items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300 px-3 disabled:opacity-50"
            >
              <Download size={18} />
              <span className="ml-2">Download Report</span>
            </button>
          </div>
        </div>
        <div className="flex-1 lg:basis-2/12 no-print">
          <Alerts hospitalId={hospId} patientId={patientId} trends />
        </div>
      </div>
    </>
  );
};

export default PatientTrendsDashboard;
